import { useEffect, useState } from "react";

import { Check, Copy, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAssignPassword } from "@/features/users/hooks/useUsers";
import type { StaffMember } from "@/features/staff/schemas/staff";

interface StaffPasswordDialogProps {
  member: StaffMember | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Regenera la contraseña de un miembro del equipo (Auth por uid).
 * La clave nueva se muestra una sola vez para copiarla y comunicarla.
 */
export function StaffPasswordDialog({
  member,
  open,
  onClose,
}: StaffPasswordDialogProps) {
  const assignPassword = useAssignPassword();
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) return;
    setGeneratedPassword(null);
    setCopied(false);
  }, [open]);

  const handleGenerate = async () => {
    if (!member) return;
    try {
      const result = await assignPassword.mutateAsync(member.uid);
      setGeneratedPassword(result.password);
      setCopied(false);
      toast.success("Contraseña regenerada. Cópiala antes de cerrar.");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "No se pudo regenerar la contraseña"
      );
    }
  };

  const handleCopy = async () => {
    if (!generatedPassword) return;
    await navigator.clipboard.writeText(generatedPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const displayName = member?.name || member?.email || "este miembro";

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" aria-hidden />
            Regenerar contraseña
          </DialogTitle>
          <DialogDescription>
            Se generará una contraseña nueva para {displayName}
            {member?.email ? (
              <>
                {" "}
                (<code className="text-xs">{member.email}</code>)
              </>
            ) : null}
            . La anterior dejará de funcionar.
          </DialogDescription>
        </DialogHeader>

        {generatedPassword ? (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-2">
            <p className="text-xs font-medium text-green-800">
              Contraseña generada (cópiala ahora; no se vuelve a mostrar):
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded border border-green-300 bg-white px-3 py-2 font-mono text-sm font-semibold text-green-900 select-all">
                {generatedPassword}
              </code>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void handleCopy()}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                <span className="ml-1.5">{copied ? "Copiado" : "Copiar"}</span>
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Comunica la clave por un canal seguro y pide que la cambie al entrar.
          </p>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={assignPassword.isPending}>
            {generatedPassword ? "Cerrar" : "Cancelar"}
          </Button>
          {!generatedPassword && (
            <Button onClick={() => void handleGenerate()} disabled={assignPassword.isPending}>
              {assignPassword.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Generar contraseña
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
